import { setSessionExpired } from "@/store/sessionExpirySlice";
import fixURI from "@/utils/fixURI";
import { PayloadAction, UnknownAction } from "@reduxjs/toolkit";
import {
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { HYDRATE } from "next-redux-wrapper";
import WeeklySpecialsResponse = WindfallRestfulResponse.ResponsePieces.WeeklySpecialsResponse;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `/rest`,
  credentials: "include",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/**
 * URL prefixes whose 401 responses trigger the global session-expired flow
 * (toast + router push to login). Scoped narrowly: only Auto Order traffic
 * for now. Other endpoints — productApi, getUser, etc. — that
 * naturally receive 401s for unauthenticated guests do NOT redirect; they
 * surface the error to their caller as before.
 *
 * Paths are checked against the request URL relative to the `/rest` baseUrl.
 * All Auto Order traffic now goes to the modern users service directly.
 */
const AUTO_ORDER_AUTH_PATH_PREFIXES = [
  "users/subscriptions", // dashboard, product/subscription actions, future-dates, subscribe
  "users/popular-products", // popular rail
];

/**
 * Wraps `fetchBaseQuery` so that HTTP 401s on Auto Order endpoints dispatch
 * the session-expired action. The `<SessionExpiryHandler />` client component
 * (mounted in `standardPage.tsx`) consumes that state and performs the
 * navigation.
 *
 * This replaces the page-local `useEffect(() => router.push(...))` pattern
 * we'd been planting in every migrated page. New Auto Order pages should
 * NOT add their own 401 effects.
 */
const baseQueryWithAuthInterceptor: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    const requestUrl = typeof args === "string" ? args : args.url;
    if (isAutoOrderAuthPath(requestUrl)) {
      const originPath = typeof window === "undefined" ? "/" : window.location.pathname + window.location.search;
      api.dispatch(setSessionExpired({ originPath }));
    }
  }

  return result;
};

function isAutoOrderAuthPath(url: string): boolean {
  // Strip any leading slashes or `/rest/` prefix that might've been included
  // by the caller — fetchBaseQuery's join handles both forms.
  const normalized = url.replace(/^\/?(rest\/)?/, "");
  return AUTO_ORDER_AUTH_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

interface MarketplaceWeeklySpecialsProduct {
  mpId: number;
  title: string;
  brandName: string;
  ppProdPriceVer: number;
  smallPhotoPath: string | null;
  detailLink: string;
  retailPrice: number;
  numberOfGeneralRatings: number;
  generalRatingSum: number;
  savingsOverRegularPrice: number | null;
}

interface MarketplaceWeeklySpecialsResponse {
  endTime: string | null;
  products: MarketplaceWeeklySpecialsProduct[];
}

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithAuthInterceptor,
  extractRehydrationInfo(action, { reducerPath }) {
    if (isHydrateAction(action)) {
      return action.payload[reducerPath];
    }
  },
  endpoints: (builder) => ({
    getTopCategories: builder.query<WindfallRestfulResponse.ResponsePieces.TopCategory[], void>({
      query: () => "category/top-categories",
      // providesTags: () => ["TOP_CATEGORIES"],
      transformResponse: (
        response: WindfallRestfulResponse.ResponsePacket<WindfallRestfulResponse.ResponsePieces.TopCategory[]>,
      ) => {
        let topCategories = response.Payload ? response.Payload : [];
        topCategories = topCategories.map((tc) => {
          return {
            ...tc,
            url: fixURI(tc.url),
          };
        });
        return topCategories;
      },
    }),
    getWeeklySpecials: builder.query<WindfallRestfulResponse.ResponsePieces.WeeklySpecialsResponse, void>({
      query: () => ({
        url: "marketplace/promotional-product/list",
        params: { name: "Weekly Specials", verId: process.env.NEXT_PUBLIC_VERTICAL_ID },
      }),
      transformResponse: (response: MarketplaceWeeklySpecialsResponse) => {
        const computeRating = (ratingSum: number, ratingCount: number) => {
          if (!ratingCount) return 0;
          return Math.ceil((ratingSum / ratingCount) * 2) / 2;
        };

        const products = (response?.products ?? []).map((p) => ({
          MpId: p.mpId,
          MpName: p.title,
          BrandName: p.brandName,
          BestPrice: p.ppProdPriceVer,
          SmallPhotoPath: p.smallPhotoPath,
          DetailLink: p.detailLink,
          RetailPrice: p.retailPrice,
          NumberOfGeneralRatings: p.numberOfGeneralRatings,
          GeneralRatingSum: p.generalRatingSum,
          Savings: p.savingsOverRegularPrice,
          Rating: computeRating(p.generalRatingSum, p.numberOfGeneralRatings),
        }));

        return { endTime: response?.endTime, products } as WeeklySpecialsResponse;
      },
    }),
  }),
});

export const {
  useGetTopCategoriesQuery,
  useLazyGetTopCategoriesQuery,
  useGetWeeklySpecialsQuery,
  util: { getRunningQueriesThunk, upsertQueryData, resetApiState, invalidateTags },
} = baseApi;
export const { getTopCategories } = baseApi.endpoints;

/* eslint-disable-next-line @typescript-eslint/no-explicit-any --- The docs for redux imply that you should be able
 * to get the AppState here and put that in for PayloadAction, ie "PayloadAction<AppState>", but that doesn't work
 * as it causes a circular reference  Because of this we have to use "any" as it can indeed any action. */
function isHydrateAction(action: UnknownAction): action is PayloadAction<any> {
  return action.type === HYDRATE;
}
